import React, { useEffect, useRef, useState } from 'react';

let hlsLoader = null;

function nativeHlsSupported() {
  if (typeof document === 'undefined') return false;
  return !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl');
}

export function preloadHlsEngine() {
  if (typeof document === 'undefined') return;
  if (nativeHlsSupported()) return;
  loadHlsConstructor().catch(() => {});
}

function loadHlsConstructor() {
  if (typeof window !== 'undefined' && window.Hls) return Promise.resolve(window.Hls);
  if (!hlsLoader) {
    hlsLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const base = process.env.PUBLIC_URL || '';
      script.src = `${base}/vendor/hls.light.min.js`;
      script.async = true;
      script.onload = () => {
        if (window.Hls) resolve(window.Hls);
        else reject(new Error('hls.js did not attach'));
      };
      script.onerror = () => reject(new Error('Failed to load hls.js'));
      document.head.appendChild(script);
    });
  }
  return hlsLoader;
}

/**
 * One muted decoder for a composed timeline HLS playlist.
 * Safari: native HLS. Chrome/Firefox: hls.js.
 * Never seeks while scrubbing — parent shows a poster instead.
 * After pause/scrub settle, video stays hidden until seeked so the poster covers the hitch.
 */
export default function NleHlsPreview({
  uri,
  playing,
  playhead,
  scrubbing = false,
  className,
  onError,
  onReady,
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const loadedUriRef = useRef('');
  const lastSeekRef = useRef(-1);
  const seekGenRef = useRef(0);
  const playingRef = useRef(!!playing);
  const scrubbingRef = useRef(!!scrubbing);
  const playheadRef = useRef(playhead);
  const onErrorRef = useRef(onError);
  const onReadyRef = useRef(onReady);
  const [shown, setShown] = useState(false);

  playingRef.current = !!playing;
  scrubbingRef.current = !!scrubbing;
  playheadRef.current = playhead;
  onErrorRef.current = onError;
  onReadyRef.current = onReady;

  const hideFrame = () => setShown(false);
  const revealFrame = (gen) => {
    if (gen != null && seekGenRef.current !== gen) return;
    if (scrubbingRef.current) return;
    setShown(true);
  };

  const seekTo = (video, t, { reveal = true } = {}) => {
    const next = Math.max(0, Number(t) || 0);
    const gen = ++seekGenRef.current;
    const already = Math.abs((video.currentTime || 0) - next) < 0.04
      && video.readyState >= 2
      && !video.seeking;
    if (already) {
      lastSeekRef.current = next;
      if (reveal) revealFrame(gen);
      return gen;
    }
    if (reveal) hideFrame();
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked);
      lastSeekRef.current = next;
      if (reveal) revealFrame(gen);
    };
    video.addEventListener('seeked', onSeeked);
    try { video.currentTime = next; } catch (_) {
      lastSeekRef.current = next;
      if (reveal) revealFrame(gen);
    }
    window.setTimeout(() => {
      if (seekGenRef.current !== gen) return;
      video.removeEventListener('seeked', onSeeked);
      lastSeekRef.current = next;
      if (reveal) revealFrame(gen);
    }, 450);
    return gen;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !uri) return undefined;
    if (loadedUriRef.current === uri && (video.src || hlsRef.current)) return undefined;

    let cancelled = false;
    let readyOnce = false;
    hideFrame();
    const startAt = Math.max(0, Number(playheadRef.current) || 0);

    const markReady = () => {
      if (cancelled || readyOnce) return;
      readyOnce = true;
      loadedUriRef.current = uri;
      onReadyRef.current?.();
      seekTo(video, startAt, { reveal: !scrubbingRef.current });
      if (playingRef.current && !scrubbingRef.current) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    };

    const fail = (msg) => {
      if (!cancelled) onErrorRef.current?.(msg || 'HLS playback error');
    };

    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.crossOrigin = 'anonymous';

    (async () => {
      if (nativeHlsSupported()) {
        video.src = uri;
        if (video.readyState >= 1) markReady();
        else video.addEventListener('loadedmetadata', markReady, { once: true });
        video.addEventListener('error', () => fail(video.error?.message), { once: true });
        return;
      }
      try {
        const Hls = await loadHlsConstructor();
        if (cancelled) return;
        if (!Hls?.isSupported?.()) {
          fail('hls.js-unsupported');
          return;
        }
        const hls = new Hls({
          enableWorker: false,
          lowLatencyMode: false,
          startPosition: startAt,
          maxBufferLength: 8,
          maxMaxBufferLength: 14,
          backBufferLength: 4,
          capLevelToPlayerSize: true,
        });
        hlsRef.current = hls;
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, markReady);
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (!data?.fatal) return;
          try { hls.destroy(); } catch (_) {}
          hlsRef.current = null;
          fail(data?.details || 'hls.js-fatal');
        });
        hls.loadSource(uri);
      } catch (err) {
        fail(err?.message || 'hls.js-load-failed');
      }
    })();

    return () => {
      cancelled = true;
      seekGenRef.current += 1;
      loadedUriRef.current = '';
      try { video.pause(); } catch (_) {}
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch (_) {}
        hlsRef.current = null;
      }
      try { video.removeAttribute('src'); video.load(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uri]);

  useEffect(() => {
    if (scrubbing) hideFrame();
  }, [scrubbing]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !uri || loadedUriRef.current !== uri) return;
    if (!playing || scrubbing) {
      video.pause();
      return;
    }
    seekTo(video, playheadRef.current, { reveal: true });
    video.play().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snap once on play, not every tick
  }, [uri, playing, scrubbing]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !uri || playing || scrubbing || loadedUriRef.current !== uri) return;
    const next = Math.max(0, Number(playhead) || 0);
    if (Math.abs(next - lastSeekRef.current) < 0.08) {
      if (!shown && video.readyState >= 2) setShown(true);
      return;
    }
    seekTo(video, next, { reveal: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uri, playing, scrubbing, playhead]);

  if (!uri) return null;
  const hidden = scrubbing || !shown;
  const dim = typeof className === 'string' && className.includes('--flash');
  return (
    <video
      ref={videoRef}
      className={className}
      muted
      playsInline
      preload="auto"
      style={{ opacity: hidden ? 0 : (dim ? 0.72 : 1), zIndex: 1 }}
    />
  );
}
